import { useCallback, useEffect, useState } from 'react'
import { Button } from '@heroui/react'
import { api } from '../../lib/api'
import { explain } from '../../lib/staffErrors'
import type { OperationProfileRow } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { EmptyState } from '../../ui/parts'
import { useStaffText } from '../../ui/StaffText'
import { useFlash } from '../sectionKit'
import { IconTile, ListCard, ListRow, ListTable, NameCell, NEW, Notice, PageHead, RowArrow, SubPageScope, toneOf, useSubPage } from '../settingsKit'
import { ImportSheet } from './ImportSheet'
import { NameSheet } from './profileSheets'
import { ProfilePage } from './ProfilePage'
import '../../styles/profiles.css'

/** The step of the import page. */
const IMPORT = 'import'

/** Administration → Operation profiles: the list, or one profile's page when openId is set. */
export function OperationProfiles({
  organisation,
  openId,
  onOpen,
  readOnly,
}: {
  organisation?: string
  openId?: string
  onOpen: (id: string) => void
  readOnly: boolean
}) {
  const { say, lang } = useStaffText()
  const [rows, setRows] = useState<OperationProfileRow[] | null>(null)
  const [error, setError] = useState('')
  const [find, setFind] = useState('')
  const { flash, toast } = useFlash()
  // Adding and importing are pages of their own (#opProfiles/new, #opProfiles/import);
  // any other step is a profile's id.
  const nav = useSubPage()
  const editing = openId === NEW ? 'add' : openId === IMPORT ? 'import' : null
  const profileId = editing ? undefined : openId

  const load = useCallback(async () => {
    try {
      const { profiles } = await api.operationProfiles(organisation)
      setRows(profiles ?? [])
      setError('')
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    }
  }, [organisation, lang])

  useEffect(() => {
    if (!profileId) void load()
  }, [load, profileId])

  // A profile just made or imported takes the place of the page that made it.
  const open = (id: string) => nav.swap(id)
  const trail = [{ label: say('profiles.section'), onOpen: nav.close }]

  if (profileId) {
    return (
      <SubPageScope at={profileId}>
        <ProfilePage
          key={profileId}
          id={profileId}
          organisation={organisation}
          readOnly={readOnly}
          onBack={() => onOpen('')}
          onOpen={open}
          onDeleted={() => {
            flash(say('profiles.list.deleted'))
            onOpen('')
          }}
        />
      </SubPageScope>
    )
  }

  if (editing === 'add' && !readOnly) {
    return (
      <NameSheet
        trail={trail}
        title={say('profiles.list.new')}
        submitLabel={say('profiles.list.create')}
        initial={{ name: '', description: '' }}
        onClose={nav.close}
        onSave={async (v) => {
          const { profile } = await api.createOperationProfile({ ...v, ...(organisation ? { organisation } : {}) })
          open(profile.id)
        }}
      />
    )
  }
  if (editing === 'import' && !readOnly && rows) {
    return <ImportSheet trail={trail} organisation={organisation} profiles={rows} onClose={nav.close} onImported={open} />
  }

  const q = find.trim().toLocaleLowerCase()
  const sorted = [...(rows ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name, lang))
    .filter((r) => !q || `${r.name} ${r.description}`.toLocaleLowerCase().includes(q))

  return (
    <>
      <PageHead
        title={say('profiles.section')}
        line={say('settings.op.line')}
        actions={
          !readOnly && (
            <>
              <Button variant="secondary" isDisabled={!rows} onPress={() => nav.open(IMPORT)}>
                <Icon name="upload" size={16} /> {say('profiles.list.import')}
              </Button>
              <Button variant="primary" isDisabled={!rows} onPress={() => nav.open(NEW)}>
                <Icon name="plus" size={16} /> {say('common.add')}
              </Button>
            </>
          )
        }
      />
      {error && <Notice>{error}</Notice>}

      <ListCard
        title={say('profiles.section')}
        count={rows ? say('settings.op.count', { n: rows.length }) : undefined}
        search={find}
        onSearch={setFind}
        searchLabel={say('settings.op.find')}
      >
        {!rows && !error && <p className="empty">{say('common.loading')}</p>}
        {rows && rows.length === 0 && <EmptyState icon={<Icon name="filter" size={20} />} title={say('profiles.list.none')} />}
        {rows && rows.length > 0 && sorted.length === 0 && <EmptyState title={say('admin.findNone', { q: find.trim() })} />}
        {sorted.length > 0 && (
          <ListTable
            label={say('profiles.section')}
            cols="minmax(240px, 1.8fr) 130px 16px"
            head={[say('settings.equipment.col.profile'), say('settings.col.branches'), '']}
          >
            {sorted.map((row) => (
              <ListRow key={row.id} onOpen={() => onOpen(row.id)}>
                <NameCell tile={<IconTile icon="filter" tone={toneOf(row.id)} />} name={row.name} sub={row.description || undefined} />
                <span role="cell" className="set-muted">
                  {say('profiles.list.branches', { n: row.branchCount })}
                </span>
                <RowArrow />
              </ListRow>
            ))}
          </ListTable>
        )}
      </ListCard>

      {toast}
    </>
  )
}
